import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useMemo, useEffect, useCallback, useState, useRef, useContext } from 'react';
import { createUseStyles, Text, Button, Link, Modal, Loader, Advice, Stepper } from 'hoi-poi-ui';
import { useWeb5Context } from "../../../lib/Web5Context";
import { getFieldsSchema, getTotalRegisters } from "../../../lib/services/imports";
import ImportType from "./steps/ImportType";
import DownloadTemplate from "./steps/DownloadTemplate";
import UploadFile from "./steps/UploadFile";
import MatchFields from "./steps/MatchFields";
import AvoidDuplicates from "./steps/AvoidDuplicates";
import MatchFieldsHeader from "./steps/MatchFieldsHeader";
import StepsHeader from "./StepHeader";
import useImportType from "./steps/hooks/useImportType";
import useUploadFile from "./steps/hooks/useUploadFile";
import useMatchFields from "./steps/hooks/useMatchFields";
import useAvoidDuplicates from "./steps/hooks/useAvoidDuplicates";
import ImportContext from "./../ImportContext";
import { intercomHideAndShow } from "../../../utils/custom";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'ImportStepper');
const STEPS_CONTENT = [ImportType, DownloadTemplate, UploadFile, MatchFields, AvoidDuplicates];
function getStepByState(state) {
  return state ? 3 : 0;
}
const initialItem = {
  importType: null,
  idType: null,
  syncNames: []
};
var _ref4 = /*#__PURE__*/React.createElement(Loader, null);
const ImportStepper = _ref => {
  let {
    entityName,
    entityLabel,
    item = initialItem,
    onExport,
    onImported,
    onCancel,
    entityLogName,
    shrinkedTab,
    maxImportColumns,
    maxImportRows,
    matchBy,
    onChangeMatchBy,
    advancedType,
    onChangeAdvancedType,
    imgImportsName,
    imgImportsFmId,
    imgImportsExtId,
    isEdit,
    idSetupRole
  } = _ref;
  const classes = useStyles();
  const {
    t,
    logEvent,
    language: lang
  } = useWeb5Context();
  const {
    language: externalLanguage,
    licenseEditionCode
  } = useContext(ImportContext);
  const language = externalLanguage || lang;
  const [data, setData] = useState(item);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [totals, setTotals] = useState(null);
  const isFirstRender = useRef(true);
  const [currentStep, setCurrentStep] = useState(() => {
    return getStepByState(item.state);
  });
  const initialSteps = useMemo(() => [{
    title: t('cfm_label_import_type'),
    isCompleted: false
  }, {
    title: t('cfm_label_import_download_title'),
    isCompleted: false
  }, {
    title: t('cfm_label_upload_file'),
    isCompleted: false
  }, {
    title: t('cfm_label_match_fields'),
    isCompleted: false
  }, {
    title: t('cfm_label_avoid_duplicate'),
    isCompleted: false
  }], [t]);
  const getInitialSteps = useCallback(newStep => {
    let step = getStepByState(item.state);
    if (newStep || !isNaN(newStep)) step = newStep;

    //To prevent mutate initialSteps array
    let steps = JSON.parse(JSON.stringify(initialSteps));

    // Completing steps
    while (step--) steps[step].isCompleted = true;
    return steps;
  }, [item === null || item === void 0 ? void 0 : item.state, initialSteps]);
  const [steps, setSteps] = useState(getInitialSteps);
  useEffect(() => {
    if (!totals && !totals !== 0) {
      getTotalRegisters(entityName).then(_ref2 => {
        let {
          data
        } = _ref2;
        return setTotals(data === null || data === void 0 ? void 0 : data.total);
      }).catch(err => {
        setTotals(0);
        console.error(err);
      });
    }
    if (steps[4]) {
      if (data.importType === 'update') {
        setSteps(steps.slice(0, -1));
        if (isFirstRender.current) {
          isFirstRender.current = false;
          setData(_objectSpread(_objectSpread({}, data), {}, {
            matchMandatory: false
          }));
        }
      } else if (isEdit && isFirstRender.current && data.importType === 'create') {
        setData(_objectSpread(_objectSpread({}, data), {}, {
          matchMandatoryStandard: true,
          matchMandatoryExtra: true
        }));
        isFirstRender.current = false;
      }
    }
    if (!steps[4] && currentStep === 1 && data.importType === 'create') {
      setSteps([...steps, {
        title: t('cfm_label_avoid_duplicate'),
        isCompleted: false
      }]);
    }
  }, [currentStep, data, getInitialSteps, steps, t, isEdit, totals, entityName]);
  const onChange = useCallback(stepData => {
    setData(_objectSpread(_objectSpread({}, data), stepData));
  }, [data, setData]);
  const resetImport = useCallback(() => {
    setData(_objectSpread({}, initialItem));
    onChangeMatchBy('');
    onChangeAdvancedType(null);
  }, [onChangeMatchBy, onChangeAdvancedType]);
  const changeCurrentStep = useCallback(step => {
    if (step <= 0) resetImport();
    setSteps(getInitialSteps(step));
    if (step < STEPS_CONTENT.length) setCurrentStep(step);
  }, [resetImport, getInitialSteps]);
  const onComplete = useCallback(stepData => {
    let s = [...steps];
    steps[currentStep].isCompleted = true;
    onChange(stepData);
    setSteps(s);
    changeCurrentStep(currentStep + 1);
  }, [steps, currentStep, onChange, changeCurrentStep]);
  const onBack = useCallback(() => {
    if (currentStep === 0) return;
    let s = [...steps];
    steps[currentStep].isCompleted = false;
    setSteps(s);
    changeCurrentStep(currentStep - 1);
  }, [currentStep, steps, changeCurrentStep]);
  const onCancelStep = useCallback(() => {
    let id = data ? data.id : null;
    onCancel(id);
  }, [data, onCancel]);
  const getFieldsList = useCallback(() => getFieldsSchema(entityName).then(_ref3 => {
    let {
      data
    } = _ref3;
    if (!data) return;
    const standard = data.standard.map(el => ({
      value: el.name,
      label: t(el.description_field)
    })).filter(el => el.value !== 'id');
    const extra = data.extra.map(el => ({
      value: el.name,
      label: t(el.description_field)
    }));
    const fields = [...standard, ...extra];
    onChange({
      fields
    });
    return fields;
  }).catch(console.warn), [entityName, onChange, t]);
  const {
    importTypeProps,
    importTypeFooterProps
  } = useImportType({
    data,
    entityName,
    logEvent,
    entityLogName,
    matchBy,
    getFieldsList,
    onChangeAdvancedType,
    onChangeMatchBy,
    onChange,
    onComplete,
    advancedType,
    currentStep
  });
  const {
    uploadFileProps,
    uploadFileHeaderProps,
    uploadFileFooterProps,
    isUploadingOpen,
    onBackUploadFile
  } = useUploadFile({
    data,
    entityName,
    getFieldsList,
    maxImportColumns,
    maxImportRows,
    onChange,
    onComplete,
    matchBy,
    shrinkedTab,
    currentStep,
    onBack,
    language
  });
  const {
    matchFieldsProps,
    matchFieldsAdviceProps,
    matchFieldsHeaderProps,
    matchFieldsFooterProps,
    matchFieldsCreateModalProps,
    onBackMatchFields,
    isMatchingOpen
  } = useMatchFields({
    data,
    t,
    onComplete,
    onBack,
    onChange,
    logEvent,
    entityName,
    entityLogName,
    entityLabel,
    onImported,
    maxImportColumns,
    maxImportRows,
    currentStep,
    idSetupRole,
    language
  });
  const {
    avoidDuplicatesProps,
    avoidDuplicatesFooterProps
  } = useAvoidDuplicates({
    entityName,
    data,
    licenseEditionCode
  });
  const Step = STEPS_CONTENT[currentStep];
  const stepHeader = useMemo(() => {
    switch (currentStep) {
      case 0:
        return /*#__PURE__*/React.createElement(StepsHeader, {
          title: t('cfm_label_import_type'),
          subtitle: t('cfm_label_import_type_subtitle')
        });
      case 1:
        return /*#__PURE__*/React.createElement(StepsHeader, {
          title: t('cfm_label_download_template'),
          subtitle: t('cfm_label_download_templated_desc', [entityLabel])
        });
      case 2:
        return /*#__PURE__*/React.createElement(StepsHeader, _extends({
          title: t('cfm_title_upload_file'),
          subtitle: t('cfm_label_upload_file_subtitle', [entityLabel])
        }, uploadFileHeaderProps));
      case 3:
        return /*#__PURE__*/React.createElement(MatchFieldsHeader, _extends({
          entityName: entityName,
          data: data
        }, matchFieldsHeaderProps));
      case 4:
      default:
        return /*#__PURE__*/React.createElement(StepsHeader, {
          title: t('cfm_title_avoid_duplicates'),
          subtitle: t('cfm_label_avoid_duplicates_subtitle')
        });
    }
  }, [currentStep, t, entityLabel, uploadFileHeaderProps, entityName, data, matchFieldsHeaderProps]);
  const matchingAdvice = useMemo(() => {
    //We only want to be able to refresh fields manually when we are editing a creation draft
    const {
      isUpdatingDraft,
      isDraftUpdated,
      updateDraft,
      currentState
    } = matchFieldsAdviceProps || {};
    if (!isEdit || data.importType !== 'create' || currentStep !== 3 || isDraftUpdated || currentState < 2) return null;
    return /*#__PURE__*/React.createElement(Advice, {
      className: classes.matchFieldsAdvice,
      type: "info",
      showIcon: true,
      showCollapse: false
    }, /*#__PURE__*/React.createElement("div", null, t('label_refresh_fields_import')), /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: () => updateDraft(true),
      isLoading: isUpdatingDraft
    }, t('action_refresh')));
  }, [matchFieldsAdviceProps, classes.matchFieldsAdvice, t, data.importType, currentStep, isEdit]);
  const openCancelModal = useCallback(() => {
    setIsCancelOpen(true);
  }, []);
  const onCloseCancel = useCallback(() => {
    setIsCancelOpen(false);
    intercomHideAndShow('show');
  }, [setIsCancelOpen]);
  const onConfirmCancel = useCallback(row => {
    onCloseCancel();
    onCancelStep();
  }, [onCloseCancel, onCancelStep]);
  const stepProps = useMemo(() => {
    const commonStepProps = {
      stepNumber: currentStep,
      entityName,
      entityLabel,
      data,
      totals
    };
    const otherStepProps = {
      onBack,
      onCancel: onCancelStep
    };
    if (currentStep === 0) return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, commonStepProps), otherStepProps), importTypeProps), {}, {
      matchBy,
      advancedType,
      imgImportsName,
      imgImportsFmId,
      imgImportsExtId
    });
    if (currentStep === 1) return _objectSpread(_objectSpread({}, commonStepProps), {}, {
      onExport
    });
    if (currentStep === 2) return _objectSpread(_objectSpread(_objectSpread({}, commonStepProps), otherStepProps), uploadFileProps);
    if (currentStep === 3) return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, commonStepProps), otherStepProps), matchFieldsProps), {}, {
      matchFieldsCreateModalProps
    });
    if (currentStep === 4) return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, commonStepProps), otherStepProps), avoidDuplicatesProps), {}, {
      entityLogName,
      onImported,
      logEvent
    });
    return {};
  }, [currentStep, entityName, entityLabel, data, totals, onBack, onCancelStep, importTypeProps, matchBy, advancedType, imgImportsName, imgImportsFmId, imgImportsExtId, onExport, uploadFileProps, matchFieldsProps, matchFieldsCreateModalProps, avoidDuplicatesProps, entityLogName, onImported, logEvent]);
  const modalProps = useMemo(() => {
    let finalModalProps = {
      confirmText: t('cfm_action_continue'),
      onRequestClose: onCancelStep
    };
    switch (currentStep) {
      case 0:
        finalModalProps = _objectSpread(_objectSpread({}, finalModalProps), importTypeFooterProps);
        break;
      case 2:
        finalModalProps = _objectSpread(_objectSpread({}, finalModalProps), uploadFileFooterProps);
        break;
      case 3:
        if (data.importType === 'update') {
          finalModalProps.confirmText = t('cfm_action_finish_process');
        }
        finalModalProps = _objectSpread(_objectSpread({}, finalModalProps), matchFieldsFooterProps);
        break;
      case 4:
        finalModalProps = _objectSpread(_objectSpread(_objectSpread({}, finalModalProps), avoidDuplicatesFooterProps), {}, {
          confirmText: t('cfm_action_finish_process')
        });
        break;
      case 1:
      default:
        finalModalProps.onConfirm = () => {
          if (data.importType === 'create') {
            logEvent({
              event: entityLogName,
              submodule: entityName,
              functionality: 'importCreateDownloadTemplate'
            });
          } else {
            logEvent({
              event: entityLogName,
              submodule: entityName,
              functionality: 'importUpdateDownloadTemplate'
            });
          }
          onComplete({});
        };
        break;
    }
    return finalModalProps;
  }, [t, currentStep, onCancelStep, onComplete, importTypeFooterProps, uploadFileFooterProps, matchFieldsFooterProps, avoidDuplicatesFooterProps, data, logEvent, entityLogName, entityName]);
  const intercomHelp = useMemo(() => {
    return /*#__PURE__*/React.createElement("div", {
      className: classes.intercomHelp
    }, /*#__PURE__*/React.createElement(Text, null, t('label_need_help')), /*#__PURE__*/React.createElement(Link, {
      className: classes.intercomHelpButton,
      variation: "primary",
      bold: true,
      onClick: () => {
        intercomHideAndShow('show');
        window.Intercom && window.Intercom('showNewMessage');
      }
    }, t('action_support_chat')));
  }, [classes.intercomHelp, classes.intercomHelpButton, t]);
  const stepFooter = useMemo(() => {
    let finalOnBack = onBack;
    if (currentStep === 2) finalOnBack = onBackUploadFile;
    if (currentStep === 3) finalOnBack = onBackMatchFields;
    return /*#__PURE__*/React.createElement("div", {
      className: classes.stepFooter
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.stepFooterLeft
    }, currentStep !== 0 && /*#__PURE__*/React.createElement(Link, {
      className: classes.backLink,
      onClick: finalOnBack,
      type: "subtitle",
      bold: true,
      variation: "primary"
    }, '< ', t('cfm_action_back_previous_step'))), /*#__PURE__*/React.createElement("div", {
      className: classes.stepFooterRight
    }, intercomHelp, /*#__PURE__*/React.createElement(Button, {
      type: "terciary",
      className: classes.cancelButton,
      onClick: openCancelModal
    }, t('cfm_action_cancel'))));
  }, [t, classes, currentStep, onBack, openCancelModal, intercomHelp, onBackMatchFields, onBackUploadFile]);
  const stepFooterConfirmText = useMemo(() => {
    if (currentStep === STEPS_CONTENT.length - 1) return t('cfm_action_finish_process');
    return t('cfm_action_leave_import_page');
  }, [t, currentStep]);
  const title = useMemo(() => {
    if (currentStep > 0 && data.importType === 'create') return t('cfm_label_import_for') + ' ' + t('cfm_label_import_type_create', [entityLabel]);
    if (currentStep > 0 && data.importType === 'update') return t('cfm_label_import_for') + ' ' + t('cfm_label_import_type_update', [entityLabel]);
    return t('cfm_title_import_entity', [entityLabel]);
  }, [currentStep, data.importType, entityLabel, t]);
  const uploadAndMatchTexts = useMemo(() => {
    let obj = {
      title: '',
      text: ''
    };
    if (isUploadingOpen) {
      obj = {
        title: t('cfm_title_uploading_file'),
        text: t('cfm_label_uploading_file_desc')
      };
    } else if (isMatchingOpen) {
      obj = {
        title: t('cfm_title_matching_with_forcemanager'),
        text: t('cfm_label_matching_with_forcemanager_desc')
      };
    }
    return obj;
  }, [t, isUploadingOpen, isMatchingOpen]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Modal, _extends({
    isOpen: true,
    size: "big",
    height: "90%",
    width: "80%",
    title: title,
    confirmText: stepFooterConfirmText,
    shouldCloseOnOverlayClick: false
  }, modalProps, {
    preComponent: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: classes.stepperWrapper
    }, /*#__PURE__*/React.createElement(Stepper, {
      className: classes.stepper,
      onClick: changeCurrentStep,
      currentStep: currentStep,
      steps: steps
    })), stepHeader, matchingAdvice),
    footerComponent: stepFooter,
    overrides: {
      content: {
        style: {
          height: '100%'
        }
      }
    }
  }), /*#__PURE__*/React.createElement(Step, stepProps)), /*#__PURE__*/React.createElement(Modal, {
    title: t('cfm_label_confirm_cancel_import'),
    size: "small",
    isOpen: isCancelOpen,
    onRequestClose: onCloseCancel,
    onCancel: onCloseCancel,
    onConfirm: onConfirmCancel,
    cancelText: t('cfm_action_cancel'),
    confirmText: t('cfm_action_leave_import_page'),
    overrides: {
      confirmButton: {
        type: 'primary-error'
      }
    }
  }, /*#__PURE__*/React.createElement(Text, {
    className: classes.modalCancelText
  }, t('cfm_label_confirm_cancel_import_desc'))), /*#__PURE__*/React.createElement(Modal, {
    title: uploadAndMatchTexts === null || uploadAndMatchTexts === void 0 ? void 0 : uploadAndMatchTexts.title,
    size: "small",
    isOpen: isUploadingOpen || isMatchingOpen,
    useCornerClose: false
  }, /*#__PURE__*/React.createElement(Text, null, uploadAndMatchTexts === null || uploadAndMatchTexts === void 0 ? void 0 : uploadAndMatchTexts.text), /*#__PURE__*/React.createElement("div", {
    className: classes.modalLoader
  }, _ref4)));
};
process.env.NODE_ENV !== "production" ? ImportStepper.propTypes = {} : void 0;
export default /*#__PURE__*/memo(ImportStepper);